<script>
import {
  formatTimestamp,
  humanizeCode,
  messageGroups,
  messageErrorKey,
  MESSAGE_STATUS_STYLE,
} from '../ticketHelpers';

// Everything the system sent about one ticket (WhatsApp messages and bell
// notifications), newest context first: rows grouped by the event that caused
// them, so a failure reads together with the alerts it raised. Admin only; the
// server refuses the call for agents.
export default {
  name: 'CoLivingMessagesPanel',
  props: {
    messages: { type: Array, default: () => [] },
    error: { type: String, default: '' },
    timezone: { type: String, default: '' },
    requester: { type: Object, default: null },
  },
  data() {
    return { statusStyle: MESSAGE_STATUS_STYLE, open: {} };
  },
  computed: {
    groups() {
      return messageGroups(this.messages);
    },
  },
  methods: {
    formatTimestamp,
    isOpen(id) {
      return !!this.open[id];
    },
    toggle(id) {
      this.$set(this.open, id, !this.open[id]);
    },
    // The tenant's name where the phone is the requester's (the ticket does
    // not carry the assignee's phone), else the kind of recipient and number.
    recipient(message) {
      const kindKey = `CO_LIVING.TICKETS.MESSAGES.TO.${String(
        message.recipient_kind
      ).toUpperCase()}`;
      const kind = this.$te(kindKey)
        ? this.$t(kindKey)
        : message.recipient_kind;
      const phone = message.recipient_phone;
      if (!phone) return kind;
      const isRequester =
        this.requester && this.requester.phone === phone && this.requester.name;
      return isRequester
        ? `${kind}: ${this.requester.name}`
        : `${kind} ${phone}`;
    },
    templateLabel(name) {
      if (!name) return '';
      const key = `CO_LIVING.TICKETS.MESSAGES.TEMPLATES.${String(name)
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')}`;
      return this.$te(key)
        ? this.$t(key)
        : humanizeCode(String(name).replace(/^pn:/, ''));
    },
    statusLabel(message) {
      // Waiting for a retry: it has failed once but is not final.
      if (
        message.status === 'pending' &&
        message.error &&
        message.attempts > 0
      ) {
        return this.$t('CO_LIVING.TICKETS.MESSAGES.RETRYING', {
          n: message.attempts,
        });
      }
      const key = `CO_LIVING.TICKETS.MESSAGES.STATUS.${String(
        message.status
      ).toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : message.status;
    },
    errorLabel(message) {
      const code = messageErrorKey(message.error);
      return code
        ? this.$t(`CO_LIVING.TICKETS.MESSAGES.ERRORS.${code}`)
        : message.error;
    },
    openChat(conversationId) {
      this.$router.push({
        name: 'inbox_conversation',
        params: { conversation_id: conversationId },
      });
    },
  },
};
</script>

<template>
  <div data-testid="co-living-messages-panel">
    <p
      v-if="error"
      class="mt-2 text-sm text-red-700 dark:text-red-300"
      data-testid="co-living-messages-error"
    >
      {{ error }}
    </p>
    <p
      v-else-if="!messages.length"
      class="mt-2 text-sm text-slate-500 dark:text-slate-400"
      data-testid="co-living-messages-empty"
    >
      {{ $t('CO_LIVING.TICKETS.MESSAGES.EMPTY') }}
    </p>
    <div
      v-else
      class="mt-2 divide-y divide-slate-100 dark:divide-slate-700"
      data-testid="co-living-messages-list"
    >
      <ul
        v-for="group in groups"
        :key="group.eventId"
        class="py-2 m-0 list-none"
        :data-testid="`co-living-message-group-${group.eventId}`"
      >
        <li
          v-for="message in group.rows"
          :key="message.id"
          class="py-1 text-sm"
          :data-testid="`co-living-message-${message.id}`"
        >
          <div class="flex flex-wrap items-center gap-2">
            <fluent-icon
              :icon="message.channel === 'portal' ? 'alert' : 'chat'"
              size="14"
              class="text-slate-500"
            />
            <span class="text-slate-500">
              {{
                formatTimestamp(message.sent_at || message.created_at, timezone)
              }}
            </span>
            <span class="text-slate-900 dark:text-slate-100">
              {{ recipient(message) }}
            </span>
            <span class="text-xs text-slate-500">
              {{ templateLabel(message.template_name) }}
            </span>
            <span
              class="px-2 py-0.5 text-xs font-medium rounded"
              :class="statusStyle[message.status]"
              :data-testid="`co-living-message-status-${message.id}`"
            >
              {{ statusLabel(message) }}
            </span>
            <button
              type="button"
              class="text-xs text-woot-600 hover:underline dark:text-woot-400"
              :aria-expanded="isOpen(message.id) ? 'true' : 'false'"
              :data-testid="`co-living-message-toggle-${message.id}`"
              @click="toggle(message.id)"
            >
              {{
                isOpen(message.id)
                  ? $t('CO_LIVING.TICKETS.MESSAGES.HIDE')
                  : $t('CO_LIVING.TICKETS.MESSAGES.SHOW')
              }}
            </button>
            <button
              v-if="message.chatwoot_conversation_id"
              type="button"
              class="text-xs text-woot-600 hover:underline dark:text-woot-400"
              :data-testid="`co-living-message-chat-${message.id}`"
              @click="openChat(message.chatwoot_conversation_id)"
            >
              {{ $t('CO_LIVING.TICKETS.MESSAGES.OPEN_CHAT') }}
            </button>
          </div>
          <p
            v-if="message.error"
            v-tooltip.top="message.error"
            class="m-0 mt-1 text-xs text-red-600 dark:text-red-400"
            :data-testid="`co-living-message-error-${message.id}`"
          >
            {{ errorLabel(message) }}
          </p>
          <p
            v-if="isOpen(message.id)"
            class="m-0 mt-1 text-xs whitespace-pre-line text-slate-500 dark:text-slate-400"
            :data-testid="`co-living-message-text-${message.id}`"
          >
            {{ message.text }}
          </p>
        </li>
      </ul>
    </div>
  </div>
</template>
